/**
 * User-settings group catalog — the canonical Zod schemas for every
 * persisted-settings area surfaced via the admin /settings page and
 * the in-widget Settings popover. Each group is independently
 * versioned + validated; the SettingsHandler refuses any update whose
 * data doesn't satisfy the matching group's schema.
 *
 * The runtime layout (one row per (user_id, group_id) with a JSON
 * `data` blob) lives in migration 0022 / `UserSettingsRow`. This file
 * is the *contract* — what shape the JSON has to take to be
 * considered valid for each group.
 *
 * Adding a new group: append it to `SETTINGS_GROUPS` below. The
 * handler's `groupId` parameter is keyed off this map, and the
 * client-side picker derives its options from the same source so a
 * new group automatically becomes editable.
 */
import { z } from 'zod';
/* ============================================================================
   Appearance
   ----------------------------------------------------------------------------
   Visual / chrome prefs that apply across surfaces (widget + admin):
   theme, default widget mode (docked vs corner mini), preferred
   bubble colour, default reply behaviour.

   `bubbleColor` is the user's *preferred* colour. The server's
   colour-spread algorithm may resolve it to a slightly-rotated value
   per project to avoid collisions with other members; that resolved
   value lives in `project_member_colors`, not here. ============================================================================ */
export const appearanceSettingsSchema = z.object({
    version: z.literal(1).default(1),
    /** Empty / undefined → use whatever the project / embedder
     *  configured (`config.ui.theme`). One of the registered ThemeId
     *  values otherwise — kept as a free-form string so older bundles
     *  reading newer settings values don't crash. */
    theme: z.string().optional(),
    /** Where the widget docks. `mini` = corner bubble; `docked` = top
     *  toolbar strip. Falls back to the project setting when unset. */
    widgetMode: z.enum(['docked', 'mini']).optional(),
    /** User's preferred bubble / marker colour. Hex `#rrggbb`. The
     *  server may rotate it per-project to avoid collisions; the
     *  resolved value lives in `project_member_colors`. */
    bubbleColor: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/, 'Must be a #rrggbb hex colour')
        .optional(),
    /** What happens when the user clicks Reply on a comment row.
     *   - expand-inline: open the reply textarea in the row (default)
     *   - open-thread:   open a dedicated thread view in the sidebar
     *   - jump-to-page:  scroll the host page to the anchored text
     *                    and surface the inline composer there */
    replyBehavior: z.enum(['expand-inline', 'open-thread', 'jump-to-page']).optional(),
});
/* ============================================================================
   Notifications
   ----------------------------------------------------------------------------
   Channel × event-type matrix. Each event type (comments, replies,
   chats, mentions) has its own toggle per channel (browser, push,
   email, sms). Defaults reflect the spec:

     - browser, push: enabled by default for everything
     - email:         enabled for comments / replies / mentions,
                      disabled for chats (chat is high-volume)
     - sms:           disabled by default everywhere; placeholder
                      for a future channel
   ============================================================================ */
const channelMatrixSchema = z.object({
    browser: z.boolean().default(true),
    push: z.boolean().default(true),
    email: z.boolean().default(true),
    sms: z.boolean().default(false),
});
export const notificationSettingsSchema = z.object({
    version: z.literal(1).default(1),
    comments: channelMatrixSchema.default({
        browser: true,
        push: true,
        email: true,
        sms: false,
    }),
    replies: channelMatrixSchema.default({
        browser: true,
        push: true,
        email: true,
        sms: false,
    }),
    chat: channelMatrixSchema.default({
        browser: true,
        push: true,
        email: false, // high-volume by nature — opt-in only
        sms: false,
    }),
    mentions: channelMatrixSchema.default({
        browser: true,
        push: true,
        email: true,
        sms: false,
    }),
    /**
     * "New user registration" — an ADMIN notification, not a per-page
     * subscription. Delivered to platform superadmins on every new account
     * and to an organization's owners/admins when someone joins their org.
     * Email-on by default (admins generally want to know); browser/push are
     * off by default. The admin Settings UI only surfaces this row for
     * superadmins + org admins — a non-admin who enables it is simply never
     * a recipient server-side.
     */
    userRegistrations: channelMatrixSchema.default({
        browser: false,
        push: false,
        email: true,
        sms: false,
    }),
});
/* ============================================================================
   Widget
   ----------------------------------------------------------------------------
   In-widget runtime behaviour the user can override. Mostly empty
   for now — a placeholder for future toggles that don't fit cleanly
   under Appearance or Notifications.
   ============================================================================ */
export const widgetSettingsSchema = z.object({
    version: z.literal(1).default(1),
    /** Open the sidebar automatically on every page load (vs only
     *  when the user toggles it). */
    sidebarAutoOpen: z.boolean().optional(),
    /** Mute the widget's hover preview tooltip on bubbles. Useful
     *  for keyboard-driven workflows. */
    disableHoverPreview: z.boolean().optional(),
});
/* ============================================================================
   Registry
   ----------------------------------------------------------------------------
   The handler reads this map at request time, picks the schema for
   the requested groupId, parses the incoming patch through it, then
   merges with the existing row's data + persists. Adding a group
   means one entry here + one entry in `SETTINGS_GROUP_DEFAULTS`.
   ============================================================================ */
export const SETTINGS_GROUPS = {
    appearance: appearanceSettingsSchema,
    notifications: notificationSettingsSchema,
    widget: widgetSettingsSchema,
};
/**
 * Defaults — what the client gets when a user has no row in a group
 * (which is the common case for a fresh account). The values match
 * each schema's `.default()` declarations so a parse with empty
 * input produces the same shape.
 */
export const SETTINGS_GROUP_DEFAULTS = {
    appearance: appearanceSettingsSchema.parse({}),
    notifications: notificationSettingsSchema.parse({}),
    widget: widgetSettingsSchema.parse({}),
};
/** Type guard for runtime group-id checks (used by the handler when
 *  it accepts a string from the wire). */
export function isSettingsGroupId(id) {
    return typeof id === 'string' && id in SETTINGS_GROUPS;
}
//# sourceMappingURL=user-settings.js.map